/**
 * HomeSimple - Trending-feed homepage
 * Renders when the API returns trending prompts successfully.
 * Delegates all section rendering to reusable home components.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { promptService } from '../services/promptService';
import { useAuth } from '../hooks/useAuth';
import { analyticsService } from '../services/analyticsService';
import Button from '../components/ui/Button';
import PromptCard from '../components/prompt/PromptCard';
import OldHomePage from './OldHomePage';

// Shared home section components
import FeatureDemoSection from '../components/home/FeatureDemoSection';
import LoginEncouragementSection from '../components/home/LoginEncouragementSection';
import SharePlatformCTA from '../components/home/SharePlatformCTA';
import InteractiveSiteTour from '../components/home/InteractiveSiteTour';
import SEO from '../components/seo/SEO';

export default function HomeSimple() {
  const { user, isAuthenticated } = useAuth();
  const [isTourOpen, setIsTourOpen] = useState(false);

  const { data: trendingData, isLoading, error } = useQuery({
    queryKey: ['prompts', 'trending', 24, 20],
    queryFn: () => promptService.getTrendingPrompts(24, 20),
    retry: 3,
  });

  useEffect(() => {
    analyticsService.trackPageView('/', 'Homepage');
  }, []);

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO 
          title="PromptShare - Discover and Share the Best AI Prompts"
          description="Explore the hottest trending AI prompts from our creative community. Copy, share, and discover prompts for Midjourney, ChatGPT, DALL-E, and more."
        />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Fallback to static page on error or no prompts ───────────────────────
  const prompts = trendingData?.prompts ?? [];
  if (error || prompts.length === 0) {
    return <OldHomePage />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="PromptShare - Discover and Share the Best AI Prompts"
        description="Explore the hottest trending AI prompts from our creative community. Copy, share, and discover prompts for Midjourney, ChatGPT, DALL-E, and more."
      />
      <div className="container mx-auto px-4 py-8 flex flex-col gap-16 md:block md:space-y-16">

        {/* Hero */}
        <div className="order-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-64 h-64 bg-white opacity-5 rounded-full pointer-events-none" aria-hidden="true" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full pointer-events-none" aria-hidden="true" />

          <div className="relative max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight">
              {isAuthenticated
                ? `Welcome back, ${user?.name}! 👋`
                : 'Discover Amazing AI Prompts'}
            </h1>
            <p className="text-primary-100 text-lg mb-8">
              {isAuthenticated
                ? 'Check out the hottest trending prompts from our community. Create, save, and share.'
                : 'Explore trending AI prompts from our creative community. Join to save, share, and chat.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/prompts">
                <Button variant="secondary" size="lg">Browse All Prompts</Button>
              </Link>
              <Button
                onClick={() => setIsTourOpen(true)}
                variant="outline"
                size="lg"
                className="!border-white !text-white hover:!bg-primary-700"
              >
                🎓 Take Guided Tour
              </Button>
              {!isAuthenticated && (
                <Link to="/register">
                  <Button variant="outline" size="lg" className="!border-white !text-white hover:!bg-primary-700">
                    Join Free →
                  </Button>
                </Link>
              )}
              {isAuthenticated && (
                <Link to="/prompts/create">
                  <Button variant="secondary" size="lg">+ Create Prompt</Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Feature demo / navigation section */}
        <div className="order-5">
          <FeatureDemoSection isAuthenticated={isAuthenticated} />
        </div>

        {/* Trending prompts grid */}
        <div className="order-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <span aria-hidden="true">🔥</span> Trending Now
              </h2>
              <p className="text-gray-600 mt-1">Hot prompts from the last 24 hours</p>
            </div>
            <Link to="/prompts">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {prompts.map((prompt) => (
              <PromptCard key={prompt.public_id} prompt={prompt} />
            ))}
          </div>
        </div>

        {/* Login encouragement (guests only) */}
        {!isAuthenticated && (
          <div className="order-4">
            <LoginEncouragementSection />
          </div>
        )}

        {/* Share the platform CTA */}
        <div className="order-5">
          <SharePlatformCTA />
        </div>

        {/* Bottom explore CTA */}
        <div className="order-3 bg-white rounded-xl border border-gray-200 p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Want to see more?</h3>
          <p className="text-gray-600 mb-6">
            Browse our full collection of prompts with advanced filtering
          </p>
          <Link to="/prompts">
            <Button size="lg">Explore All Prompts</Button>
          </Link>
        </div>

        {/* Collapsible FAQ Section for users & SEO crawlers */}
        <div className="order-6 bg-white rounded-xl border border-gray-200 p-8">
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is PromptShare?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "PromptShare is a premier community-driven platform for discovering, copying, and sharing high-quality AI prompts for models like Midjourney, ChatGPT, DALL-E, Stable Diffusion, and more."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I copy prompts on PromptShare?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Simply browse to any prompt details page, and click the 'Copy Prompt' button. You can then paste it directly into ChatGPT, Midjourney, or any other AI client."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can I share my own AI prompts?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, absolutely! Anyone can join for free, create prompts, upload visual previews, categorize them, and get feedback and likes from the community."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is PromptShare free to use?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, PromptShare is 100% free to search, discover, copy, and share AI prompts."
                  }
                }
              ]
            })}
          </script>

          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h3>
          <div className="space-y-4 max-w-4xl mx-auto">
            <FAQItem
              q="What is PromptShare?"
              a="PromptShare is a premier community-driven platform for discovering, copying, and sharing high-quality AI prompts for models like Midjourney, ChatGPT, DALL-E, Stable Diffusion, and more."
            />
            <FAQItem
              q="How do I copy prompts on PromptShare?"
              a="Simply browse to any prompt details page, and click the 'Copy Prompt' button. You can then paste it directly into ChatGPT, Midjourney, or any other AI client."
            />
            <FAQItem
              q="Can I share my own AI prompts?"
              a="Yes, absolutely! Anyone can join for free, create prompts, upload visual previews, categorize them, and get feedback and likes from the community."
            />
            <FAQItem
              q="Is PromptShare free to use?"
              a="Yes, PromptShare is 100% free to search, discover, copy, and share AI prompts."
            />
          </div>
        </div>

        {/* Custom premium onboarding tour */}
        <InteractiveSiteTour isOpen={isTourOpen} onClose={() => setIsTourOpen(false)} />

      </div>
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left py-3 font-medium text-gray-900 hover:text-primary-600 transition-colors"
      >
        <span>{q}</span>
        <span className="ml-6 flex-shrink-0 text-gray-500 font-bold text-lg">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="mt-2 text-gray-600 text-sm leading-relaxed transition-all duration-300">
          {a}
        </div>
      )}
    </div>
  );
}

